import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../client';
import type { PaginatedResponse } from '../types/common';
import type { User, UserCreatePayload, UserUpdatePayload } from '../types/user';

/** Query keys */
export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...usersKeys.lists(), filters] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: string) => [...usersKeys.details(), id] as const,
};

/** Fetch users with optional filters */
export function useUsers(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: usersKeys.list(filters),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<PaginatedResponse<User>>('/api/v1/users/', {
        params: filters,
      });
      return response.data;
    },
  });
}

/** Fetch a single user */
export function useUser(id: string) {
  return useQuery({
    queryKey: usersKeys.detail(id),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<User>(`/api/v1/users/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

/** Create a new user */
export function useCreateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: UserCreatePayload) => {
      const client = getApiClient();
      const response = await client.post<User>('/api/v1/users/', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}

/** Update an existing user */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UserUpdatePayload }) => {
      const client = getApiClient();
      const response = await client.patch<User>(`/api/v1/users/${id}/`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.setQueryData(usersKeys.detail(data.id), data);
    },
  });
}

/** Delete a user */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const client = getApiClient();
      await client.delete(`/api/v1/users/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}
